'use client'

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { toast } from 'sonner'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  componentName?: string
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  errorId: string | null
}

/**
 * Enhanced error boundary specifically designed to catch and debug React errors,
 * including the minified React Error #418 and other rendering issues.
 */
class ReactErrorBoundary extends Component<Props, State> {
  private retryCount = 0
  private maxRetries = 3

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    return {
      hasError: true,
      error,
      errorId
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { componentName = 'Unknown Component' } = this.props
    
    // Enhanced error logging for React Error #418 and other issues
    console.group(`🚨 React Error Boundary - ${componentName}`)
    console.error('Error caught:', error)
    console.error('Error info:', errorInfo)
    console.error('Component stack:', errorInfo.componentStack)
    
    // Check for specific React errors
    if (error.message.includes('Minified React error #418')) {
      console.error('🔴 REACT ERROR #418 DETECTED:')
      console.error('This is likely a hydration mismatch or invalid DOM nesting')
      console.error('Common causes:')
      console.error('- Server and client rendering different content')
      console.error('- Invalid HTML nesting (e.g., <p> inside <p>)')
      console.error('- Browser extensions modifying DOM')
      console.error('- Conditional rendering based on client-only state')
    }
    
    if (error.message.includes('globalAudioManager')) {
      console.error('🔴 GLOBAL AUDIO MANAGER ERROR:')
      console.error('Audio manager not properly initialized')
    }
    
    // Log additional debugging information
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentName,
      timestamp: new Date().toISOString(),
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'Server',
      url: typeof window !== 'undefined' ? window.location.href : 'Server'
    })
    
    console.groupEnd()
    
    // Update state with error info
    this.setState({
      errorInfo
    })
    
    // Call the onError callback if provided
    this.props.onError?.(error, errorInfo)
    
    // Show user-friendly toast notification
    toast.error(`Component error in ${componentName}. Check console for details.`)
    
    // Attempt automatic recovery for certain errors
    if (this.retryCount < this.maxRetries) {
      if (error.message.includes('hydration') || error.message.includes('418')) {
        console.log(`🔄 Attempting automatic recovery (${this.retryCount + 1}/${this.maxRetries})`)
        setTimeout(() => {
          this.handleReset()
        }, 1000)
      }
    }
  }

  handleReset = (): void => {
    this.retryCount++
    console.log(`🔄 Resetting error boundary (attempt ${this.retryCount})`)
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    })
    
    // Force a re-render by updating a key or triggering parent re-render
    if (typeof window !== 'undefined') {
      // Clear any potential DOM inconsistencies
      setTimeout(() => {
        console.log('✅ Error boundary reset complete')
      }, 100)
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const { componentName = 'Component' } = this.props
      
      // If a custom fallback UI is provided, render it
      if (this.props.fallback) {
        return this.props.fallback
      }
      
      // Otherwise, render a detailed fallback UI
      return (
        <div className="react-error-boundary p-4 rounded-md bg-red-50 border border-red-200 m-2">
          <h3 className="text-red-800 font-medium mb-2">
            ⚠️ {componentName} Error
          </h3>
          <p className="text-red-600 mb-4">
            A rendering error occurred in this component.
          </p>
          
          {this.state.error?.message.includes('418') && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <h4 className="text-yellow-800 font-medium mb-1">React Error #418 Detected</h4>
              <p className="text-yellow-700 text-sm">
                This is likely a hydration mismatch. Try refreshing the page or disabling browser extensions.
              </p>
            </div>
          )}
          
          <div className="flex gap-2 mb-4">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
              disabled={this.retryCount >= this.maxRetries}
            >
              {this.retryCount >= this.maxRetries ? 'Max Retries Reached' : 'Try Again'}
            </button>
            
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
            >
              Refresh Page
            </button>
          </div>
          
          <details className="mt-4">
            <summary className="text-sm text-red-600 cursor-pointer font-medium">
              Error Details (ID: {this.state.errorId})
            </summary>
            <div className="mt-2 p-3 bg-red-100 rounded text-xs overflow-auto max-h-40">
              <div className="mb-2">
                <strong>Error:</strong> {this.state.error?.toString()}
              </div>
              {this.state.errorInfo && (
                <div>
                  <strong>Component Stack:</strong>
                  <pre className="whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        </div>
      )
    }

    // If there's no error, render the children
    return this.props.children
  }
}

export default ReactErrorBoundary